import React, { memo, useContext, useMemo } from 'react';
import { connect } from 'react-redux';

import { AppContext } from 'src/components/app-context';
import { Column, Row } from '@prisjakt/ui/list/variants';

import { ColumnWithGutter, ListMobileColumnElseRow } from 'src/ui/list-layouts';
import CategoryListing from '../category-listing';
import FeaturedProducts from '../featured-products';
import PremiumBanner from '../premium-banner';
import ProductListing from '../product-listing';
import VideoListing from '../video-listing';

import type {
  BrandPageFragment,
  PremiumBrandContentFragment,
  VideoListingFragment,
} from 'src/types';
import type { BrandContentProps } from '..';
import * as BrandStyles from '../styles';
import * as S from './styles';

const PremiumBrandContent = ({
  id,
  name,
  logo,
  externalUri,
  externalUriText,
  brandLinkClick,
  products,
  categories,
  categoryTree,
  header,
  description,
  featuredProducts,
  media,
  analyticsAccessLink,
}: BrandContentProps &
  Pick<BrandPageFragment, 'header' | 'description'> &
  Pick<PremiumBrandContentFragment, 'featuredProducts'> &
  Pick<VideoListingFragment, 'media'>) => {
  const { t } = useContext(AppContext);
  const brand = useMemo(() => ({ id, name, logo }), [id, name, logo]);
  const titleLogo = useMemo(
    () =>
      logo ? <BrandStyles.Image width={70} alt={name} src={logo} /> : null,
    [name, logo]
  );
  const rawDescription = useMemo(
    () => ({ __html: description as string }),
    [description]
  );

  return (
    <S.Header>
      <PremiumBanner
        brandName={name}
        banner={header}
        label={t('brand', 'premium-brand.information-notice', { brand: name })}
      />
      <ColumnWithGutter>
        <Row>
          <BrandStyles.Header titleLogo={titleLogo} title={name}>
            {analyticsAccessLink && (
              <BrandStyles.Analytics>
                {analyticsAccessLink}
              </BrandStyles.Analytics>
            )}
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a
              onClick={brandLinkClick}
              target="_BLANK"
              rel="nofollow noopener"
              href={(externalUri || externalUriText)!}
            >
              {externalUriText}
            </a>
          </BrandStyles.Header>
        </Row>
        <ListMobileColumnElseRow equallySizedChildren gap={1}>
          <Column>
            <FeaturedProducts
              title={t('brand', 'premium-brand.featured-products')}
              products={featuredProducts}
            />
          </Column>
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={rawDescription}
          />
        </ListMobileColumnElseRow>
      </ColumnWithGutter>
      <VideoListing videos={media.videos} />
      <ProductListing products={products} brandName={name} />
      <CategoryListing
        categories={categories}
        categoryTree={categoryTree}
        brand={brand}
      />
    </S.Header>
  );
};

export default connect(state => state)(memo(PremiumBrandContent));
